import { useMemo } from 'react';

import { useTemplateContextFieldsQuery } from '@/services/api/templatesV2ServiceEnhanced';

interface SmsPreviewProps {
  body: string;
  customValues?: Record<string, string>;
}

const SMS_SEGMENT_SIZE = 160;

/**
 * Renders an SMS template body with sample values in a phone message bubble UI.
 */
export const SmsPreview = ({ body, customValues }: SmsPreviewProps) => {
  const { data: contextFields } = useTemplateContextFieldsQuery();

  const sampleValues = useMemo(() => {
    if (!contextFields) return {} as Record<string, string>;
    const map: Record<string, string> = {};
    contextFields.categories.forEach((cat) => {
      cat.fields.forEach((f) => {
        map[f.key] = f.sample;
      });
    });
    return map;
  }, [contextFields]);

  const mergedValues = useMemo(() => {
    if (!customValues) return sampleValues;
    return { ...sampleValues, ...customValues };
  }, [sampleValues, customValues]);

  const renderedText = useMemo(() => {
    if (!body) return '';
    return body.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, varName: string) => {
      return mergedValues[varName] ?? match;
    });
  }, [body, mergedValues]);

  const charCount = renderedText.length;
  const segments = Math.ceil(charCount / SMS_SEGMENT_SIZE) || 1;

  if (!body) {
    return (
      <div className="flex h-full flex-col">
        <div className="px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Preview
          </span>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm font-medium text-zinc-500">
            Enter content above to see preview
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Preview
        </span>
      </div>

      {/* Phone mockup */}
      <div className="flex flex-1 items-start justify-center px-4 py-4">
        <div className="w-full max-w-[320px] rounded-2xl border border-zinc-200 bg-zinc-100 p-4 dark:border-zinc-700 dark:bg-zinc-800">
          {/* Top bar */}
          <div className="mb-4 text-center">
            <p className="text-xs font-semibold text-zinc-500">Messages</p>
            <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Robert Smith
            </p>
          </div>

          {/* Message bubble */}
          <div className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl bg-[#007AFF] px-3 py-2">
              <p className="whitespace-pre-wrap text-sm font-medium text-white">
                {renderedText}
              </p>
            </div>
          </div>

          {/* Timestamp */}
          <div className="mt-1 text-right">
            <span className="text-[10px] font-medium text-zinc-500">Now</span>
          </div>
        </div>
      </div>

      {/* Character count and segment count */}
      <div className="border-t border-zinc-200 px-3 py-2 dark:border-zinc-700">
        <span className="text-[11px] font-medium tabular-nums text-zinc-500">
          {charCount} chars · {segments} segment{segments !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};
